import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize } from 'rxjs';
import { HttpService } from '../../../services/http.service';
import { CustomToastComponent } from '../../toast/custom-toast.component';
import { AddStudentComponent } from '../add-student/add-student.component';

interface StudentItem {
  studentId: string;
  fullName: string;
}

export interface AssignStudentsDialogData {
  eventId: string;
  endpoint?: string; // 'events' (default) or 'TeacherEvent'
}

@Component({
  selector: 'app-assign-students-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule],
  templateUrl: './assign-students-dialog.component.html',
  styleUrls: ['./assign-students-dialog.component.scss'],
})
export class AssignStudentsDialogComponent implements OnInit {
  searchQuery: string = '';
  isLoading: boolean = false;
  isSubmitting: boolean = false;

  allStudents: StudentItem[] = [];
  assignedStudents: StudentItem[] = [];
  schoolID: string = '';

  constructor(
    public dialogRef: MatDialogRef<AssignStudentsDialogComponent>,
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: AssignStudentsDialogData,
  ) {}

  ngOnInit(): void {
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    this.schoolID = userDetails?.schoolId ?? '';
    this.fetchStudents();
  }

  fetchStudents(): void {
    this.isLoading = true;
    const role = localStorage.getItem('userRole');
    const url =
      role === 'teacher'
        ? `teacherstudent/${this.schoolID}/students`
        : `events/events/${this.data.eventId}/students-dropdown`;

    this.httpService
      .get<any>(url)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (response) => {
          const items: any[] = response?.data?.items ?? response?.data ?? [];

          if (Array.isArray(items)) {
            this.allStudents = items.map((s: any) => ({
              studentId: s.studentId,
              fullName: s.fullName || `${s.firstName} ${s.lastName}`,
            }));
          }
        },
        error: () => this.showErrorToast('Failed to load students.'),
      });
  }

  get isSearching(): boolean {
    return this.searchQuery.trim().length > 0;
  }

  get availableStudents(): StudentItem[] {
    const assignedIds = new Set(this.assignedStudents.map((s) => s.studentId));
    const q = this.searchQuery.trim().toLowerCase();
    if (!q) return [];
    return this.allStudents.filter(
      (s) =>
        !assignedIds.has(s.studentId) && s.fullName.toLowerCase().includes(q),
    );
  }

  addStudent(student: StudentItem): void {
    this.assignedStudents.push(student);
  }

  removeStudent(student: StudentItem): void {
    this.assignedStudents = this.assignedStudents.filter(
      (s) => s.studentId !== student.studentId,
    );
  }

  openAddNewStudent(): void {
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    const schoolId = userDetails?.schoolId ?? '';
    const role = localStorage.getItem('userRole');
    const preselectedSchoolId =
      this.data.endpoint === 'TeacherEvent' || role === 'teacher'
        ? schoolId
        : '';
    this.dialog
      .open(AddStudentComponent, {
        width: '50.5rem',
        maxWidth: '90vw',
        minWidth: '300px',
        data: {
          EditMode: false,
          schoolID: preselectedSchoolId,
          eventId: this.data.eventId,
          addStudent: false,
          ViewMode: false,
          endpoint: this.data.endpoint ?? 'events',
        },
        disableClose: true,
        autoFocus: false,
        panelClass: 'custom-dialog-container',
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.dialogRef.close(true);
        }
      });
  }

  onSubmit(): void {
    if (this.assignedStudents.length === 0) {
      this.showErrorToast('Please select at least one student.');
      return;
    }
    this.isSubmitting = true;
    const studentIds = this.assignedStudents.map((s) => s.studentId);
    const endpoint = this.data.endpoint ?? 'events';
    this.httpService
      .post<any>(`${endpoint}/${this.data.eventId}/assign-existing`, {
        studentIds,
      })
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (response) => {
          if (response?.status) {
            this.showSuccessToast(
              response.message ?? 'Students assigned successfully.',
            );
            this.dialogRef.close(true);
          } else {
            this.showErrorToast(
              response?.message ?? 'Failed to assign students.',
            );
          }
        },
        error: (err) =>
          this.showErrorToast(err?.message ?? 'Something went wrong.'),
      });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'success-toast'],
    });
  }

  private showErrorToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      data: { message, type: 'error' },
      panelClass: ['no-padding-snackbar', 'error-toast'],
    });
  }
}
